import { ReactNode } from "react";

import { InfoCircle } from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import SearchInput from "components/SearchInput";
import useCanManageIdpGroups from "shared/IdpGroup/access/useCanManageIdpGroups";

import { SettingsContext } from "../Context";
import { getManagementStrategy } from "../helpers";
import UserManagementActivationBadge from "../UserManagementActivationBadge";
import UserManagementWarningCallout from "../UserManagementWarningCallout";
import { showIdpGroupMappingInfoDrawer } from "./InfoDrawer";
import { showIdpGroupMappingMapGroupDrawer } from "./MapGroupDrawer";
import { FILTERS_ORDER_SETTINGS_KEY } from "./constants";

type IdpGroupMappingPageLayoutProps = {
  children: ReactNode;
  hasActiveSearch?: boolean;
};

const IdpGroupMappingPageLayout = ({
  children,
  hasActiveSearch,
}: IdpGroupMappingPageLayoutProps) => {
  const { activationStatus } = useTypedContext(SettingsContext);
  const canManageIdpGroups = useCanManageIdpGroups();

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationIdpGroupMapping,
    defaultCallbackTrackProperties: {
      managementStrategy: getManagementStrategy(activationStatus),
    },
  });

  const handleOpenInfoDrawer = () => {
    trackSegmentAnalyticsEvent("Info Click");
    showIdpGroupMappingInfoDrawer();
  };

  const handleOpenMapIdpGroupDrawer = () => {
    trackSegmentAnalyticsEvent("Open map IdP group");
    showIdpGroupMappingMapGroupDrawer({ activationStatus });
  };

  return (
    <>
      <UserManagementWarningCallout
        analyticsPage={AnalyticsPageOrganization.OrganizationIdpGroupMapping}
      />
      <PageInfoHeaderWrapper>
        <Box align="center" __deprecatedGap="1.1rem">
          <PageInfoHeaderTitle title="IdP group mapping" />
          <ButtonIcon onPress={handleOpenInfoDrawer} icon={InfoCircle} variant="ghost">
            Open info drawer
          </ButtonIcon>
          <UserManagementActivationBadge />
        </Box>
        <PageInfoHeaderActions>
          {hasActiveSearch && (
            <SearchInput
              placeholder="Search..."
              filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
            />
          )}
          {canManageIdpGroups && (
            <Button onPress={handleOpenMapIdpGroupDrawer} variant="primary">
              Map IdP group
            </Button>
          )}
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <ListContentWrapper>{children}</ListContentWrapper>
    </>
  );
};

export default IdpGroupMappingPageLayout;
